import { useLocation } from "react-router-dom";

/**
 * The Home brief's hand-off to `/tests/new` (C10#14).
 *
 * The brief travels as router state, the same contract the chat and thread
 * hand-offs already use (`ChatPage` reads `initialMessage` off location
 * state), never as a URL query: a query lands in browser history and in the
 * pageview tracker's `pathname?search`. This module owns the state's shape:
 * `studyBriefHandoffState` is its only writer and `useStudyBriefHandoff` its
 * only reader.
 *
 * The state lives on the history entry Home pushed. The builder's
 * `router.replace('?draftId=')` drops it, which is intended: by then the brief
 * is the draft's first chat turn and the draft id is what a reload reopens.
 */
export interface StudyBriefHandoffState {
  initialMessage: string;
}

/** The router state for a brief, or none when the brief is blank. */
export function studyBriefHandoffState(
  message: string,
): StudyBriefHandoffState | undefined {
  const initialMessage = message.trim();
  return initialMessage ? { initialMessage } : undefined;
}

/** The brief Home handed to this page, or null when it handed none. */
export function useStudyBriefHandoff(): string | null {
  const state = useLocation().state as Partial<StudyBriefHandoffState> | null;
  const brief = state?.initialMessage;
  return typeof brief === "string" && brief.trim() ? brief : null;
}
